import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest, mfaStepUpAuthority } from "./msalConfig";
import { getDeviceFingerprint } from "../utils/deviceFingerprint";

const AuthContext = createContext(null);

/** B2C custom policies are conventionally named like ".../B2C_1A_MFA" --
 * the last path segment is the policy name that shows up in the ID token's
 * "tfp" (or "acr") claim after a successful run of that policy. */
function mfaPolicyName() {
  return mfaStepUpAuthority?.split("/").filter(Boolean).pop();
}

/**
 * Same B2C/MSAL shape as web's AuthContext, plus `forbidden`: the signed-in
 * B2C account may be valid but not a member of the admin group (checked
 * server-side, see api/services/auth.py's _is_admin) -- every 403 from the
 * API flips this so the app can show "you don't have admin access" instead
 * of silently failing per-request.
 *
 * `jitRequired` is the sibling flag for the narrower case: the account IS an
 * admin, but this route additionally requires an active, second-admin-
 * approved app.JITAccessRequests grant (secure_endpoint's require_jit) and
 * the caller doesn't have one. apiClient's response interceptor flips this
 * on the distinct `jit_access_required` error_code (as opposed to the
 * generic `forbidden`), and AuthedApp (App.jsx) renders AccessRequestPage
 * instead of the normal routes while it's set -- same gating shape as
 * `forbidden`/ForbiddenPage, just a different destination.
 */
export function AuthProvider({ children }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [forbidden, setForbidden] = useState(false);
  const [jitRequired, setJitRequired] = useState(false);
  const [lastReauthAt, setLastReauthAt] = useState(null);

  const account = accounts[0] || null;

  // Detects "the account just came back from a step-up MFA redirect" by
  // checking which B2C policy actually issued this ID token, rather than
  // just trusting the redirect succeeded -- a plain silent token refresh
  // shouldn't count as a fresh re-auth for useRequireReauth's elevation
  // window.
  useEffect(() => {
    const claims = account?.idTokenClaims;
    const policy = claims?.tfp || claims?.acr;
    if (policy && policy === mfaPolicyName()) {
      setLastReauthAt(Date.now());
    }
  }, [account]);

  const login = useCallback(() => instance.loginRedirect(loginRequest), [instance]);
  const logout = useCallback(() => instance.logoutRedirect(), [instance]);

  /** Redirects to the MFA step-up B2C policy. Called both by apiClient's
   * 401 mfa_required handler and directly by hooks/useRequireReauth.js
   * before a sensitive admin action. B2C's step-up is a full-page redirect,
   * so there is no seamless "resume the action after re-auth" here -- the
   * admin re-clicks the action once they're back. */
  const stepUpMfa = useCallback(() => {
    return instance.loginRedirect({ ...loginRequest, authority: mfaStepUpAuthority });
  }, [instance]);

  const markReauthenticated = useCallback(() => setLastReauthAt(Date.now()), []);

  const getAccessToken = useCallback(async () => {
    if (!account) throw new Error("Not authenticated");
    try {
      const result = await instance.acquireTokenSilent({ ...loginRequest, account });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(loginRequest);
        return null;
      }
      throw err;
    }
  }, [instance, account]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      account,
      login,
      logout,
      stepUpMfa,
      getAccessToken,
      deviceFingerprint: getDeviceFingerprint(),
      forbidden,
      setForbidden,
      jitRequired,
      setJitRequired,
      lastReauthAt,
      markReauthenticated,
    }),
    [isAuthenticated, account, login, logout, stepUpMfa, getAccessToken, forbidden, jitRequired, lastReauthAt, markReauthenticated]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
